import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../../_core/_services/notification.service';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { ModalCleanupService } from '../../../_core/_services/modal-cleanup.service';

@Component({
  selector: 'app-admin-policy-fields',
  standalone: false,
  templateUrl: './admin-policy-fields.html',
  styleUrl: './admin-policy-fields.css',
})
export class AdminPolicyFields implements OnInit {
  loading = false;
  saving = false;
  importing = false;
  deletingId: string | null = null;
  error: string | null = null;

  policyId = '';
  policy: any = null;
  policies: any[] = [];
  items: any[] = [];

  form!: FormGroup;
  importForm!: FormGroup;
  editingId: string | null = null;
  fieldPendingDelete: any = null;

  fieldTypes = [
    'text',
    'textarea',
    'number',
    'date',
    'select',
    'radio',
    'checkbox',
    'email',
    'phone'
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: ComplianceAdminService,
    private notifyService: NotificationService,
    private modalCleanup: ModalCleanupService,
  ) {}

  ngOnInit(): void {
    this.policyId = this.route.snapshot.paramMap.get('id') || '';

    this.form = this.fb.group({
      fieldKey: ['', [Validators.required, Validators.maxLength(100)]],
      label: ['', [Validators.required, Validators.maxLength(150)]],
      fieldType: ['text', [Validators.required]],
      isRequired: [true],
      isEncrypted: [false],
      sortOrder: [0, [Validators.required, Validators.min(0)]],
      minLength: [null],
      maxLength: [null],
      regexPattern: [''],
      placeholder: [''],
      helpText: [''],
      optionsJson: [''],
      validationJson: [''],

      options: this.fb.array([]),

      minDateMode: [''],
      minDate: [''],
      maxDateMode: [''],
      maxDate: ['']
    });

    this.importForm = this.fb.group({
      sourcePolicyId: ['', [Validators.required]],
      overwriteExisting: [false]
    });

    if (!this.policyId) {
      this.error = 'Policy not found.';
      return;
    }

    this.loadPolicy();
    this.loadPolicies();
    this.loadItems();
  }

  loadPolicy(): void {
    this.adminService.getPolicyById(this.policyId).subscribe({
      next: (res: any) => {
        this.policy = res?.data || res || null;
      },
      error: () => {
        this.policy = null;
      }
    });
  }

  loadPolicies(): void {
    this.adminService.getPolicies({ page: 1, pageSize: 1000 }).subscribe({
      next: (res: any) => {
        this.policies = res?.data || res || [];
      },
      error: () => {
        this.policies = [];
      }
    });
  }

  loadItems(): void {
    this.loading = true;
    this.error = null;

    this.adminService.getPolicyFields(this.policyId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          this.items = res?.data || res || [];
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load policy fields.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  importFields(): void {
    this.importForm.markAllAsTouched();

    if (this.importForm.invalid) return;

    const sourcePolicyId = String(this.importForm.value.sourcePolicyId || '').trim();

    if (!sourcePolicyId || sourcePolicyId === this.policyId) return;

    this.importing = true;
    this.error = null;

    this.adminService.getPolicyFields(sourcePolicyId)
      .subscribe({
        next: (res: any) => {
          const sourceFields = res?.data || res || [];

          if (!sourceFields.length) {
            this.importing = false;
            this.notifyService.showInfo('The selected policy has no fields to import.', 'No Fields');
            return;
          }

          const overwriteExisting = !!this.importForm.value.overwriteExisting;
          const requests = sourceFields
            .map((field: any) => this.buildImportRequest(field, overwriteExisting))
            .filter(Boolean);

          if (!requests.length) {
            this.importing = false;
            this.notifyService.showInfo('All selected policy fields already exist on this policy.', 'Nothing Imported');
            return;
          }

          forkJoin(requests)
            .pipe(finalize(() => (this.importing = false)))
            .subscribe({
              next: () => {
                this.notifyService.showSuccess('Policy fields imported successfully.', 'Success');
                this.loadItems();
              },
              error: (err) => {
                this.error = err?.error?.message || 'Failed to import policy fields.';
                this.notifyService.showError(this.error!, 'Error');
              }
            });
        },
        error: (err) => {
          this.importing = false;
          this.error = err?.error?.message || 'Failed to load source policy fields.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  private buildImportRequest(field: any, overwriteExisting: boolean): any {
    const payload = {
      fieldKey: String(field.fieldKey || '').trim(),
      label: String(field.label || '').trim(),
      fieldType: String(field.fieldType || 'text').trim(),
      isRequired: field.isRequired ?? true,
      isEncrypted: field.isEncrypted ?? false,
      sortOrder: Number(field.sortOrder || 0),
      minLength: this.asNullableNumber(field.minLength),
      maxLength: this.asNullableNumber(field.maxLength),
      regexPattern: this.asNullableString(field.regexPattern),
      placeholder: this.asNullableString(field.placeholder),
      helpText: this.asNullableString(field.helpText),
      optionsJson: this.asNullableString(field.optionsJson),
      validationJson: this.asNullableString(field.validationJson)
    };

    if (!payload.fieldKey || !payload.label) return null;

    const existing = this.items.find(x =>
      String(x.fieldKey || '').trim().toUpperCase() === payload.fieldKey.toUpperCase()
    );

    if (existing?.id) {
      return overwriteExisting
        ? this.adminService.updatePolicyField(this.policyId, existing.id, payload)
        : null;
    }

    return this.adminService.createPolicyField(this.policyId, payload);
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    if (!this.validateFieldTypeConfig()) {
      return;
    }

    this.saving = true;
    this.error = null;

    const fieldType = String(this.form.value.fieldType || 'text').trim();

    const optionsJson = this.buildOptionsJson(fieldType);
    const validationJson = this.buildValidationJson(fieldType);

    const payload = {
      fieldKey: String(this.form.value.fieldKey || '').trim(),
      label: String(this.form.value.label || '').trim(),
      fieldType,
      isRequired: !!this.form.value.isRequired,
      isEncrypted: !!this.form.value.isEncrypted,
      sortOrder: Number(this.form.value.sortOrder || 0),
      minLength: this.asNullableNumber(this.form.value.minLength),
      maxLength: this.asNullableNumber(this.form.value.maxLength),
      regexPattern: this.asNullableString(this.form.value.regexPattern),
      placeholder: this.asNullableString(this.form.value.placeholder),
      helpText: this.asNullableString(this.form.value.helpText),

      optionsJson,
      validationJson,

      options: fieldType.toLowerCase() === 'select' ? this.getOptionRows() : null,
      validation: fieldType.toLowerCase() === 'date'
        ? {
            minDate: this.resolveDateValue(this.form.value.minDateMode, this.form.value.minDate),
            maxDate: this.resolveDateValue(this.form.value.maxDateMode, this.form.value.maxDate)
          }
        : null
    };

    const request$ = this.editingId
      ? this.adminService.updatePolicyField(this.policyId, this.editingId, payload)
      : this.adminService.createPolicyField(this.policyId, payload);

    request$
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess(
            this.editingId ? 'Policy field updated successfully.' : 'Policy field created successfully.',
            'Success'
          );
          this.resetForm();
          this.loadItems();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to save policy field.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  edit(item: any): void {
    this.editingId = item.id;

    this.form.patchValue({
      fieldKey: item.fieldKey || '',
      label: item.label || '',
      fieldType: item.fieldType || 'text',
      isRequired: item.isRequired ?? true,
      isEncrypted: item.isEncrypted ?? false,
      sortOrder: item.sortOrder ?? 0,
      minLength: item.minLength,
      maxLength: item.maxLength,
      regexPattern: item.regexPattern || '',
      placeholder: item.placeholder || '',
      helpText: item.helpText || '',
      optionsJson: item.optionsJson || '',
      validationJson: item.validationJson || '',
      minDateMode: '',
      minDate: '',
      maxDateMode: '',
      maxDate: ''
    });

    this.hydrateOptions(item);
    this.hydrateDateValidation(item);
  }

  openDeleteModal(item: any): void {
    if (!item?.id) return;
    this.fieldPendingDelete = item;
  }

  confirmDelete(): void {
    if (!this.fieldPendingDelete?.id) return;

    const item = this.fieldPendingDelete;
    this.deletingId = item.id;
    this.error = null;

    this.adminService.deletePolicyField(this.policyId, item.id)
      .pipe(finalize(() => (this.deletingId = null)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess('Policy field deleted successfully.', 'Success');
          this.closeDeleteModal();
          this.fieldPendingDelete = null;

          if (this.editingId === item.id) {
            this.resetForm();
          }

          this.loadItems();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to delete policy field.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  closeDeleteModal(): void {
    this.modalCleanup.hideById('deletePolicyFieldModal');
  }

  resetForm(): void {
    this.editingId = null;

    this.form.reset({
      fieldKey: '',
      label: '',
      fieldType: 'text',
      isRequired: true,
      isEncrypted: false,
      sortOrder: 0,
      minLength: null,
      maxLength: null,
      regexPattern: '',
      placeholder: '',
      helpText: '',
      optionsJson: '',
      validationJson: '',
      minDateMode: '',
      minDate: '',
      maxDateMode: '',
      maxDate: ''
    });

    this.options.clear();
  }

  back(): void {
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'edit']);
  }

  asNullableString(value: any): string | null {
    const text = String(value || '').trim();
    return text ? text : null;
  }

  asNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  openFields(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'fields']);
  }

  openDocuments(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'documents']);
  }

  openRules(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'rules']);
  }

  get options(): FormArray {
    return this.form.get('options') as FormArray;
  }

  isSelectField(): boolean {
    return String(this.form?.value?.fieldType || '').toLowerCase() === 'select';
  }

  isDateField(): boolean {
    return String(this.form?.value?.fieldType || '').toLowerCase() === 'date';
  }

  addOption(label = '', value = ''): void {
    this.options.push(this.fb.group({
      label: [label, [Validators.required]],
      value: [value, [Validators.required]]
    }));
  }

  removeOption(index: number): void {
    this.options.removeAt(index);
  }

  private getOptionRows(): any[] {
    return this.options.controls
      .map(control => ({
        label: String(control.value.label || '').trim(),
        value: String(control.value.value || '').trim()
      }))
      .filter(x => x.label || x.value);
  }

  private buildOptionsJson(fieldType: string): string | null {
    if (fieldType.toLowerCase() !== 'select') {
      return this.asNullableString(this.form.value.optionsJson);
    }

    const rows = this.getOptionRows();

    return rows.length
      ? JSON.stringify(rows)
      : null;
  }

  private buildValidationJson(fieldType: string): string | null {
    if (fieldType.toLowerCase() !== 'date') {
      return this.asNullableString(this.form.value.validationJson);
    }

    const payload = {
      minDate: this.resolveDateValue(this.form.value.minDateMode, this.form.value.minDate),
      maxDate: this.resolveDateValue(this.form.value.maxDateMode, this.form.value.maxDate)
    };

    return payload.minDate || payload.maxDate
      ? JSON.stringify(payload)
      : null;
  }

  private resolveDateValue(mode: string, customDate: string): string | null {
    if (mode === 'today') {
      return 'today';
    }

    if (mode === 'custom') {
      return this.asNullableString(customDate);
    }

    return null;
  }

  private validateFieldTypeConfig(): boolean {
    const fieldType = String(this.form.value.fieldType || '').toLowerCase();

    if (fieldType === 'select') {
      const rows = this.getOptionRows();

      if (!rows.length) {
        this.notifyService.showError('Select fields must have at least one option.', 'Validation Error');
        return false;
      }

      if (rows.some(x => !x.label || !x.value)) {
        this.notifyService.showError('Each select option must have both label and value.', 'Validation Error');
        return false;
      }

      const values = rows.map(x => x.value.toUpperCase());
      const uniqueValues = new Set(values);

      if (values.length !== uniqueValues.size) {
        this.notifyService.showError('Select option values must be unique.', 'Validation Error');
        return false;
      }
    }

    if (fieldType === 'date') {
      const minDate = this.resolveDateValue(this.form.value.minDateMode, this.form.value.minDate);
      const maxDate = this.resolveDateValue(this.form.value.maxDateMode, this.form.value.maxDate);

      if (minDate && minDate !== 'today' && !this.isValidDateString(minDate)) {
        this.notifyService.showError('Minimum date must be today or a valid date.', 'Validation Error');
        return false;
      }

      if (maxDate && maxDate !== 'today' && !this.isValidDateString(maxDate)) {
        this.notifyService.showError('Maximum date must be today or a valid date.', 'Validation Error');
        return false;
      }
    }

    return true;
  }

  private isValidDateString(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
  }

  private hydrateOptions(item: any): void {
    this.options.clear();

    const source = Array.isArray(item.options)
      ? item.options
      : this.parseJsonArray(item.optionsJson);

    source.forEach((option: any) => {
      if (typeof option === 'string') {
        this.addOption(option, option);
        return;
      }

      this.addOption(option?.label || '', option?.value || '');
    });

    if (this.isSelectField() && this.options.length === 0) {
      this.addOption();
    }
  }

  private hydrateDateValidation(item: any): void {
    const validation = item.validation || this.parseJsonObject(item.validationJson);

    this.applyDateControl('minDate', validation?.minDate);
    this.applyDateControl('maxDate', validation?.maxDate);
  }

  private applyDateControl(prefix: 'minDate' | 'maxDate', value: string | null | undefined): void {
    const modeControl = `${prefix}Mode`;

    if (!value) {
      this.form.patchValue({
        [modeControl]: '',
        [prefix]: ''
      });
      return;
    }

    if (value === 'today') {
      this.form.patchValue({
        [modeControl]: 'today',
        [prefix]: ''
      });
      return;
    }

    this.form.patchValue({
      [modeControl]: 'custom',
      [prefix]: value
    });
  }

  private parseJsonArray(value: string | null | undefined): any[] {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private parseJsonObject(value: string | null | undefined): any {
    if (!value) return null;

    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch {
      return null;
    }
  }


  get availableImportPolicies(): any[] {
    return this.policies.filter(x => String(x.id) !== String(this.policyId));
  }

  get sourcePolicyId() { return this.importForm.get('sourcePolicyId'); }
  get fieldKey() { return this.form.get('fieldKey'); }
  get label() { return this.form.get('label'); }
}
